import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tasksApi, usersApi } from '../../services/api';
import Modal from '../shared/Modal';
import toast from 'react-hot-toast';

interface NewTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewTaskModal({ isOpen, onClose }: NewTaskModalProps) {
  const [title, setTitle] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [deadline, setDeadline] = useState('');

  const queryClient = useQueryClient();
  const { data: usersData } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });
  const users = usersData?.data || [];

  const mutation = useMutation({
    mutationFn: () => tasksApi.create({ title, assigned_to: assignedTo, deadline: deadline || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Tarefa criada!');
      onClose();
      setTitle('');
      setAssignedTo('');
      setDeadline('');
    },
    onError: (err: any) => toast.error(err.message || 'Erro ao criar tarefa'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !assignedTo) return;
    mutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova Tarefa"
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
          <button
            type="submit"
            form="new-task-form"
            disabled={!title.trim() || !assignedTo || mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Salvando...' : 'Criar Tarefa'}
          </button>
        </div>
      }
    >
      <form id="new-task-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Descrição da tarefa <span className="text-red-500">*</span></label>
          <textarea
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="input resize-none"
            rows={3}
            placeholder="Descreva a tarefa..."
            required
            autoFocus
          />
        </div>

        <div>
          <label className="label">Atribuir a <span className="text-red-500">*</span></label>
          <select
            value={assignedTo}
            onChange={e => setAssignedTo(e.target.value)}
            className="input"
            required
          >
            <option value="">Selecione uma pessoa...</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name} — {u.role_label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Prazo (opcional)</label>
          <input
            type="date"
            value={deadline}
            onChange={e => setDeadline(e.target.value)}
            className="input"
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
      </form>
    </Modal>
  );
}
